import React, { useEffect, useState, useContext, useMemo } from 'react';
import { ConsultaPermissoesMemoria, SalvaPermissoesMemoria, ConsultaNotasMemoria } from '../../lib/api';
import { GlobalContext } from '../../lib/GlobalVar';
import Swal from 'sweetalert2';

// F19 - Permissoes da Memoria (default-deny) por VAULT/NOTA, CONEXAO (ssh) e BANCO (sql), ler|editar,
// por setor. TI e admin fora da matriz (acesso total, locked). Grava o formato v2 que o conector le
// (via ponte HTTP /admin/*: a matriz vive no servidor do conector, fonte de verdade do RBAC).

const VAZIO = { ler: false, editar: false, sql: false, notas: {} };

// Pill de toggle (ler/editar). `derivado` = ligado por implicacao (editar => ler), fica travado.
function Toggle({ on, off, label, derivado, disabled, onClick }) {
    const ativo = on || derivado;
    return (
        <button type="button"
            onClick={disabled || derivado ? undefined : onClick}
            title={derivado ? 'implicado por Editar' : (ativo ? 'concedido' : 'sem acesso')}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                cursor: disabled || derivado ? 'default' : 'pointer',
                border: '1px solid var(--ink)',
                background: ativo ? 'var(--ink)' : 'transparent',
                color: ativo ? 'var(--bg)' : 'var(--ink)',
                opacity: disabled ? 0.35 : (derivado ? 0.7 : 1),
            }}>
            <span style={{
                width: 9, height: 9, borderRadius: '50%',
                background: ativo ? 'var(--bg)' : 'transparent',
                border: ativo ? 'none' : '1px solid var(--ink)',
            }} />
            {label}
        </button>
    );
}

export default function TIPermissoesMemoria() {
    const { user: me } = useContext(GlobalContext);
    const [setores, setSetores] = useState([]);
    const [vaults, setVaults] = useState([]);
    const [conexoes, setConexoes] = useState([]);
    const [bancos, setBancos] = useState([]);
    const [matriz, setMatriz] = useState({});
    const [meta, setMeta] = useState({});
    const [setorAtivo, setSetorAtivo] = useState(null);
    const [salvando, setSalvando] = useState(false);

    // Notas por vault (lazy): { [vault]: { loading, notas:[], pastas:[] } }
    const [notasVault, setNotasVault] = useState({});
    const [expandido, setExpandido] = useState({});   // { [vault]: bool }
    const [busca, setBusca] = useState({});            // { [vault]: string }

    const cred = () => ({ solicitante_id: me?.id, solicitante: me?.email });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { carregar(); }, []);

    async function carregar() {
        const r = await ConsultaPermissoesMemoria(cred());
        if (r.Error) return Swal.fire({ icon: 'warning', title: 'Atencao', text: r.Error });
        setSetores(r.setores || []);
        setVaults(r.vaults || []);
        setConexoes(r.conexoes || []);
        setBancos(r.bancos || []);
        setMatriz(r.matriz || {});
        setMeta({ atualizado_em: r.atualizado_em, atualizado_por: r.atualizado_por });
        setSetorAtivo(prev => prev || (r.setores || []).find(s => s !== 'TI') || null);
    }

    const ehTIativo = setorAtivo === 'TI';

    function grant(fonte) {
        return (matriz[setorAtivo] && matriz[setorAtivo][fonte]) || VAZIO;
    }

    // Atualiza um campo (ler|editar) de uma fonte, respeitando editar => ler.
    function setFonte(fonte, campo) {
        if (ehTIativo) return;
        setMatriz(prev => {
            const setor = { ...(prev[setorAtivo] || {}) };
            const g = { ...VAZIO, ...(setor[fonte] || {}), notas: { ...((setor[fonte] || {}).notas || {}) } };
            if (campo === 'editar') {
                g.editar = !g.editar;
                if (g.editar) g.ler = true;         // editar implica ler
            } else if (campo === 'ler') {
                g.ler = !g.ler;
                if (!g.ler) g.editar = false;       // sem ler => sem editar
            } else if (campo === 'sql') {
                g.sql = !g.sql;
            }
            setor[fonte] = g;
            return { ...prev, [setorAtivo]: setor };
        });
    }

    function setNota(fonte, rel, campo) {
        if (ehTIativo) return;
        setMatriz(prev => {
            const setor = { ...(prev[setorAtivo] || {}) };
            const g = { ...VAZIO, ...(setor[fonte] || {}), notas: { ...((setor[fonte] || {}).notas || {}) } };
            const n = { ler: false, editar: false, ...(g.notas[rel] || {}) };
            if (campo === 'editar') { n.editar = !n.editar; if (n.editar) n.ler = true; }
            else { n.ler = !n.ler; if (!n.ler) n.editar = false; }
            if (n.ler || n.editar) g.notas[rel] = n; else delete g.notas[rel];
            setor[fonte] = g;
            return { ...prev, [setorAtivo]: setor };
        });
    }

    async function toggleExpand(vault) {
        const abrir = !expandido[vault];
        setExpandido(p => ({ ...p, [vault]: abrir }));
        if (abrir && !notasVault[vault]) {
            setNotasVault(p => ({ ...p, [vault]: { loading: true, notas: [], pastas: [] } }));
            const r = await ConsultaNotasMemoria({ ...cred(), vault });
            if (r.Error) {
                setNotasVault(p => ({ ...p, [vault]: { loading: false, notas: [], pastas: [], erro: r.Error } }));
            } else {
                setNotasVault(p => ({ ...p, [vault]: { loading: false, notas: r.notas || [], pastas: r.pastas || [] } }));
            }
        }
    }

    async function salvar() {
        setSalvando(true);
        const r = await SalvaPermissoesMemoria({ ...cred(), matriz });
        setSalvando(false);
        if (r.Error) return Swal.fire({ icon: 'warning', title: 'Atencao', text: r.Error });
        Swal.fire({ icon: 'success', title: 'Permissoes salvas', timer: 1200, showConfirmButton: false });
        carregar();
    }

    // Conta grants concedidos no setor ativo (fontes com algum acesso).
    const resumo = useMemo(() => {
        const m = matriz[setorAtivo] || {};
        let fontesCom = 0, notasCom = 0;
        Object.keys(m).forEach(f => {
            const g = m[f] || {};
            const nk = Object.keys(g.notas || {}).length;
            if (g.ler || g.editar || g.sql || nk) fontesCom++;
            notasCom += nk;
        });
        return { fontesCom, notasCom };
    }, [matriz, setorAtivo]);

    // Linha de fonte simples (conexao/banco): nome + descricao + toggles Leitura/Escrita.
    function LinhaFonte({ f }) {
        const g = grant(f.nome);
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 4px', borderBottom: '1px solid var(--line)' }}>
                <span style={{ flex: 1, minWidth: 200 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{f.nome}</span>
                    {(f.descricao || f.servidor) && <span className="muted" style={{ display: 'block', fontSize: 11.5, marginTop: 2 }}>{f.descricao || f.servidor}</span>}
                </span>
                <Toggle label="Leitura" on={g.ler} derivado={g.editar} onClick={() => setFonte(f.nome, 'ler')} />
                <Toggle label="Escrita" on={g.editar} onClick={() => setFonte(f.nome, 'editar')} />
            </div>
        );
    }

    return (
        <div className="page">
            <div className="page-head">
                <div>
                    <div className="eyebrow"><span className="num ti">TI</span> Permissoes da Memoria</div>
                    <h1 className="page-title">Permissoes da Memoria</h1>
                    <p className="page-sub">
                        Controle <strong>default-deny</strong> do conector Memoria Babita: por setor, libere <strong>vaults</strong> (notas),
                        <strong> conexões</strong> (servidores) e <strong>bancos</strong> — cada um em <em>leitura</em>/<em>escrita</em>. Nada e liberado por padrao.
                        TI e Administrador tem acesso total (fora da matriz).
                    </p>
                </div>
                <button className="btn" disabled={salvando || ehTIativo} onClick={salvar}>
                    {salvando ? 'Salvando...' : 'Salvar permissoes'}
                </button>
            </div>

            <div className="kpi-strip">
                <div className="kpi-cell"><div className="kpi-label">Setores</div><div className="kpi-val">{Math.max(setores.length - 1, 0)}</div></div>
                <div className="kpi-cell"><div className="kpi-label">Conexões</div><div className="kpi-val">{vaults.length + conexoes.length + bancos.length}</div></div>
                <div className="kpi-cell"><div className="kpi-label">Setor ativo</div><div className="kpi-val">{setorAtivo || '-'}</div></div>
                <div className="kpi-cell"><div className="kpi-label">Fontes liberadas</div><div className="kpi-val">{ehTIativo ? '∞' : resumo.fontesCom}</div></div>
                <div className="kpi-cell"><div className="kpi-label">Notas liberadas</div><div className="kpi-val">{ehTIativo ? '∞' : resumo.notasCom}</div></div>
            </div>

            <div className="card flat" style={{ padding: 16, marginTop: 16 }}>
                <div className="eyebrow" style={{ marginBottom: 10 }}>Setor</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {setores.map(s => (
                        <button key={s} onClick={() => setSetorAtivo(s)} className="btn"
                            style={{
                                padding: '6px 14px',
                                background: setorAtivo === s ? 'var(--ink)' : 'transparent',
                                color: setorAtivo === s ? 'var(--bg)' : 'var(--ink)',
                                border: '1px solid var(--ink)',
                                opacity: s === 'TI' ? 0.6 : 1,
                            }}
                            title={s === 'TI' ? 'TI tem acesso total sempre (fora da matriz)' : ''}>
                            {s}{s === 'TI' && <span>&nbsp;🔒</span>}
                        </button>
                    ))}
                </div>
            </div>

            {ehTIativo && (
                <div className="card flat" style={{ marginTop: 16, padding: '18px 22px' }}>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-soft)' }}>
                        <strong>TI</strong> e <strong>Administrador</strong> tem acesso total ao conector (admin fora da matriz).
                        Nao ha o que configurar aqui — selecione outro setor.
                    </p>
                </div>
            )}

            {setorAtivo && !ehTIativo && (
                <>
                    {/* VAULTS + NOTAS */}
                    <div className="card flat" style={{ padding: 16, marginTop: 16 }}>
                        <div className="eyebrow" style={{ marginBottom: 12 }}>Vaults de notas (.md)</div>
                        {vaults.map(v => {
                            const g = grant(v);
                            const nk = Object.keys(g.notas || {}).length;
                            const info = notasVault[v];
                            const q = (busca[v] || '').trim().toLowerCase();
                            const notasFiltradas = (info?.notas || []).filter(n => !q || n.rel.toLowerCase().includes(q));
                            const vaultLiberado = g.ler || g.editar;
                            return (
                                <div key={v} style={{ border: '1px solid var(--line)', borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 14px', background: 'var(--surface-soft, transparent)' }}>
                                        <button type="button" onClick={() => toggleExpand(v)}
                                            style={{ border: 'none', background: 'transparent', color: 'var(--ink)', padding: 0, fontWeight: 700, fontSize: 14, cursor: 'pointer', textAlign: 'left' }}>
                                            <span style={{ display: 'inline-block', width: 14 }}>{expandido[v] ? '▾' : '▸'}</span> {v}
                                        </button>
                                        <div style={{ flex: 1 }} />
                                        {nk > 0 && !vaultLiberado && (
                                            <span className="mono muted" style={{ fontSize: 11 }}>{nk} nota(s) liberada(s)</span>
                                        )}
                                        <Toggle label="Ler vault" on={g.ler} derivado={g.editar} onClick={() => setFonte(v, 'ler')} />
                                        <Toggle label="Editar vault" on={g.editar} onClick={() => setFonte(v, 'editar')} />
                                    </div>

                                    {expandido[v] && (
                                        <div style={{ padding: '12px 14px', borderTop: '1px solid var(--line)' }}>
                                            {vaultLiberado && (
                                                <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                                                    Vault liberado por inteiro — grants por nota abaixo ficam redundantes.
                                                </div>
                                            )}
                                            <input
                                                value={busca[v] || ''}
                                                onChange={e => setBusca(p => ({ ...p, [v]: e.target.value }))}
                                                placeholder="Buscar nota por caminho..."
                                                style={{ width: '100%', maxWidth: 380, padding: '7px 10px', border: '1px solid var(--line)', borderRadius: 8, marginBottom: 10, fontSize: 13 }}
                                            />
                                            {info?.loading && <div className="muted" style={{ fontSize: 12 }}>Carregando notas...</div>}
                                            {info?.erro && <div style={{ fontSize: 12, color: 'var(--danger, #c00)' }}>{info.erro}</div>}
                                            {info && !info.loading && !info.erro && notasFiltradas.length === 0 && (
                                                <div className="muted" style={{ fontSize: 12 }}>Nenhuma nota .md {q ? 'para a busca' : 'neste vault'}.</div>
                                            )}
                                            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                                                {agruparPorPasta(notasFiltradas).map(grp => (
                                                    <div key={grp.pasta} style={{ marginBottom: 8 }}>
                                                        {grp.pasta && <div className="mono muted" style={{ fontSize: 11, margin: '6px 0 3px' }}>{grp.pasta}/</div>}
                                                        {grp.itens.map(n => {
                                                            const ng = (g.notas || {})[n.rel] || {};
                                                            return (
                                                                <div key={n.rel} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '4px 0 4px 10px' }}>
                                                                    <span style={{ flex: 1, minWidth: 160, fontSize: 13 }}>{n.nome}</span>
                                                                    <Toggle label="Ler" on={!!ng.ler} derivado={!!ng.editar || vaultLiberado} onClick={() => setNota(v, n.rel, 'ler')} disabled={vaultLiberado} />
                                                                    <Toggle label="Editar" on={!!ng.editar} derivado={vaultLiberado && g.editar} onClick={() => setNota(v, n.rel, 'editar')} disabled={vaultLiberado} />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {!vaults.length && <div className="muted" style={{ fontSize: 12 }}>Nenhum vault reportado pelo conector.</div>}
                    </div>

                    {/* CONEXOES (SSH) */}
                    <div className="card flat" style={{ padding: 16, marginTop: 16 }}>
                        <div className="eyebrow" style={{ marginBottom: 12 }}>Conexões (servidores)</div>
                        {conexoes.map(c => <LinhaFonte key={c.nome} f={c} />)}
                        {!conexoes.length && <div className="muted" style={{ fontSize: 12 }}>Nenhuma conexão SSH reportada pelo conector.</div>}
                        <div className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>
                            <strong>Leitura</strong>: ler/listar/buscar arquivos no servidor. <strong>Escrita</strong>: criar/editar/apagar arquivos.
                            O terminal (shell / tool <code className="mono">executar</code>) permanece restrito a TI/Administrador.
                        </div>
                    </div>

                    {/* BANCOS (SQL) */}
                    <div className="card flat" style={{ padding: 16, marginTop: 16 }}>
                        <div className="eyebrow" style={{ marginBottom: 12 }}>Bancos (SQL)</div>
                        {bancos.map(b => <LinhaFonte key={b.nome} f={b} />)}
                        {!bancos.length && <div className="muted" style={{ fontSize: 12 }}>Nenhum banco reportado pelo conector.</div>}
                        <div className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>
                            <strong>Leitura</strong> habilita as tools <code className="mono">sql*</code> (consultar). <strong>Escrita</strong> registra o nivel
                            de gravacao — a trava somente-leitura do conector segue bloqueando qualquer escrita real no banco.
                        </div>
                    </div>
                </>
            )}

            <div className="card flat" style={{ marginTop: 24, padding: '18px 22px' }}>
                <div className="eyebrow" style={{ marginBottom: 8 }}><span className="num">i</span> Como funciona</div>
                <p style={{ fontSize: 12.5, color: 'var(--ink-soft)', lineHeight: 1.7, margin: 0 }}>
                    Modelo <strong>default-deny</strong>: um setor so acessa o que voce liberar aqui. Libere um <strong>vault</strong> inteiro
                    (Ler/Editar), <strong>notas</strong> especificas, uma <strong>conexão</strong> (servidor) ou um <strong>banco</strong>.
                    <strong> Editar</strong> implica <strong>Ler</strong>. A matriz e gravada (AES) no servidor do conector, no formato <code className="mono">versao:2</code>
                    que o RBAC le a cada chamada — muda na hora, sem relogar.
                    {meta.atualizado_em && <> Ultima alteracao: <span className="mono">{new Date(meta.atualizado_em).toLocaleString('pt-BR')}</span> por {meta.atualizado_por || '-'}.</>}
                </p>
            </div>
        </div>
    );
}

// Agrupa notas por pasta preservando a ordem alfabetica ja vinda do backend.
function agruparPorPasta(notas) {
    const mapa = new Map();
    for (const n of notas) {
        if (!mapa.has(n.pasta)) mapa.set(n.pasta, []);
        mapa.get(n.pasta).push(n);
    }
    return [...mapa.entries()].map(([pasta, itens]) => ({ pasta, itens }));
}
